/**
 * Calculate attendance for a specific student across all courses
 * Supports the new array-based session format:
 *   attendance.sessions = [{ date, branch, sessionNumber, time, attendees, totalStudents, presentCount }]
 *
 * @param {string} studentId - The student ID to check attendance for
 * @param {Array} coursesData - The array of course data objects
 * @returns {Object} - The attendance statistics for the student
 */
function calculateAttendance(studentId, coursesData) {
  // Create result object structure
  const result = {
    studentId,
    attendanceByCourse: {},
    overall: {
      totalSessions: 0,
      presentCount: 0,
      attendancePercentage: 0,
    },
  };

  // Track overall counts
  let totalSessionsCount = 0;
  let totalPresentCount = 0;

  // Process each course
  coursesData.forEach((course) => {
    const courseName = course.title;
    const attendance = course.attendance;

    // Initialize course statistics
    result.attendanceByCourse[courseName] = {
      totalSessions: 0,
      presentCount: 0,
      attendancePercentage: 0,
      sessionDates: [],
    };

    // Process attendance only if it exists
    if (attendance && attendance.sessions && Array.isArray(attendance.sessions)) {
      // New array format
      attendance.sessions.forEach((session) => {
        const attendees = session.attendees || [];
        if (attendees.length > 0) {
          // Valid session with at least one student present
          result.attendanceByCourse[courseName].totalSessions++;
          totalSessionsCount++;

          if (attendees.includes(studentId)) {
            result.attendanceByCourse[courseName].presentCount++;
            totalPresentCount++;

            // Extract date part for the session dates list
            const dateValue = (session.date || "").includes("T")
              ? session.date.split("T")[0]
              : session.date;
            result.attendanceByCourse[courseName].sessionDates.push(dateValue);
          }
        }
      });

      // Calculate percentage for this course
      const sessionCount = result.attendanceByCourse[courseName].totalSessions;
      if (sessionCount > 0) {
        result.attendanceByCourse[courseName].attendancePercentage = (
          (result.attendanceByCourse[courseName].presentCount / sessionCount) *
          100
        ).toFixed(2);
      }
    } else if (attendance && typeof attendance === 'object' && !Array.isArray(attendance)) {
      // Legacy fallback: attendance is an object with session keys
      // This handles the case where attendance.sessions is not set but attendance itself is the sessions object
      Object.entries(attendance).forEach(([sessionKey, students]) => {
        if (sessionKey === 'sessions') return; // skip the sessions key itself
        if (Array.isArray(students) && students.length > 0) {
          result.attendanceByCourse[courseName].totalSessions++;
          totalSessionsCount++;

          if (students.includes(studentId)) {
            result.attendanceByCourse[courseName].presentCount++;
            totalPresentCount++;

            const dateValue = sessionKey.split("_")[0];
            result.attendanceByCourse[courseName].sessionDates.push(dateValue);
          }
        }
      });

      const sessionCount = result.attendanceByCourse[courseName].totalSessions;
      if (sessionCount > 0) {
        result.attendanceByCourse[courseName].attendancePercentage = (
          (result.attendanceByCourse[courseName].presentCount / sessionCount) *
          100
        ).toFixed(2);
      }
    }
  });

  // Calculate overall attendance percentage
  if (totalSessionsCount > 0) {
    result.overall.totalSessions = totalSessionsCount;
    result.overall.presentCount = totalPresentCount;
    result.overall.attendancePercentage = (
      (totalPresentCount / totalSessionsCount) *
      100
    ).toFixed(2);
  }

  return result;
}

export default calculateAttendance;
